import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, InputGroup } from 'react-bootstrap';
import { FaArrowLeft, FaSave, FaEye, FaEyeSlash, FaPlus, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, FaLock, FaCaretDown, FaUserCheck } from 'react-icons/fa';
import Select from 'react-select';
import './AddDSO.css'; // Make sure this CSS file exists or remove if not needed
import { useNavigate } from 'react-router-dom';
import { rmApi, dsoApi } from '../../services/api.js'; // Import dsoApi
import locationService from '../../services/location.js';

const AddDSO = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    state: '',
    city: '',
    pincode: '',
    applicantType: '',
    individualType: '',
    businessType: '',
    password: '',
    confirmPassword: '',
    relationshipManager: ''
  });
  
  const [validated, setValidated] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [passwordStrength, setPasswordStrength] = useState('');
  const [relationshipManagers, setRelationshipManagers] = useState([]);
  const [isLoadingRm, setIsLoadingRm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); // Add submitting state
  
  // States, cities, and pincodes will be fetched from API
  const [states, setStates] = useState([]);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  
  // Individual Types
  const individualTypes = [
    'Salaried', 
    'Self Employed'
  ];

  // Business Types
  const businessTypes = [
    'Sole Proprietorship',
    'Partnership',
    'Limited Liability Partnership (LLP)',
    'Private Limited Company',
    'Public Limited Company',
    'One Person Company',
    'Non-Profit Organization',
    'Trust',
    'Hindu Undivided Family'
  ];
  
  // Cities and pincodes will be fetched based on selection
  const [cities, setCities] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  
  const [pincodes, setPincodes] = useState([]);
  const [isLoadingPincodes, setIsLoadingPincodes] = useState(false);
  
  // Fetch relationship managers and states on component mount
  useEffect(() => {
    fetchRelationshipManagers();
    fetchStates();
  }, []);
  
  // Function to fetch relationship managers from the API
  const fetchRelationshipManagers = async () => {
    setIsLoadingRm(true);
    
    try {
      const response = await rmApi.getRelationshipManagers({
        limit: 100, // Fetch a large number to get all active RMs
        status: 'Active' // Only fetch active RMs
      });
      
      // Transform data for react-select
      const rmOptions = response.data.items
        .filter(rm => rm.status === 'Active') // Filter active RMs (in case API doesn't filter properly)
        .map(rm => ({
          value: rm.id,
          label: rm.name,
          email: rm.email,
          location: rm.location
        }));
      
      setRelationshipManagers(rmOptions);
    } catch (error) {
      console.error('Error fetching relationship managers:', error);
      setErrorMessage('Failed to load relationship managers. Please try again.');
      setShowError(true);
    } finally {
      setIsLoadingRm(false);
    }
  };

  // Fetch states from API
  const fetchStates = async () => {
    setIsLoadingStates(true);
    try {
      const response = await locationService.getAllStates();
      const stateOptions = response.data.states.map(state => ({
        value: state.id,
        label: state.name,
        code: state.code
      }));
      setStates(stateOptions);
    } catch (error) {
      console.error('Error fetching states:', error);
      setErrorMessage('Failed to fetch states');
      setShowError(true);
    } finally {
      setIsLoadingStates(false);
    }
  };

  // Fetch cities by state ID
  const fetchCitiesByState = async (stateId) => {
    if (!stateId) return;
    
    setIsLoadingCities(true);
    setCities([]);
    setPincodes([]);
    
    try {
      const response = await locationService.getCitiesByState(stateId);
      const cityOptions = response.data.cities.map(city => ({
        value: city.id,
        label: city.name
      }));
      setCities(cityOptions);
    } catch (error) {
      console.error('Error fetching cities:', error);
      setErrorMessage('Failed to fetch cities for the selected state');
      setShowError(true);
    } finally {
      setIsLoadingCities(false);
    }
  };

  // Fetch pincodes by city ID
  const fetchPincodesByCity = async (cityId) => {
    if (!cityId) return;
    
    setIsLoadingPincodes(true);
    setPincodes([]);
    
    try {
      const response = await locationService.getPincodesByCity(cityId);
      const pincodeOptions = response.data.pincodes.map(pincode => ({
        value: pincode.id,
        label: pincode.code
      }));
      setPincodes(pincodeOptions);
    } catch (error) {
      console.error('Error fetching pincodes:', error);
      setErrorMessage('Failed to fetch pincodes for the selected city');
      setShowError(true);
    } finally {
      setIsLoadingPincodes(false);
    }
  };

  // Update cities when state changes
  const handleStateChange = (selectedOption) => {
    setFormData({
      ...formData,
      state: selectedOption ? selectedOption.value : '',
      city: '', // Reset city when state changes
      pincode: '' // Reset pincode when state changes
    });
    
    if (selectedOption) {
      fetchCitiesByState(selectedOption.value);
    } else {
      setCities([]);
      setPincodes([]);
    }
  };
  
  // Update pincodes when city changes
  const handleCityChange = (selectedOption) => {
    setFormData({
      ...formData,
      city: selectedOption ? selectedOption.value : '',
      pincode: '' // Reset pincode when city changes
    });
    
    if (selectedOption) {
      fetchPincodesByCity(selectedOption.value);
    } else {
      setPincodes([]);
    }
  };
  
  // Handle applicant type change
  const handleApplicantTypeChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      applicantType: value,
      individualType: '',
      businessType: ''
    });
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Check password strength when password field changes
    if (name === 'password') {
      checkPasswordStrength(value);
    }
    
    // Check if passwords match when either password field changes
    if (name === 'password' || name === 'confirmPassword') {
      if (name === 'password') {
        setPasswordMatch(value === formData.confirmPassword);
      } else {
        setPasswordMatch(formData.password === value);
      }
    }
  };
  
  // Handle react-select change for relationship manager
  const handleRMChange = (selectedOption) => {
    setFormData({
      ...formData,
      relationshipManager: selectedOption ? selectedOption.value : ''
    });
  };
  
  // Check password strength
  const checkPasswordStrength = (password) => {
    if (!password) {
      setPasswordStrength('');
      return;
    }
    
    let strength = 0;
    
    // Length check
    if (password.length >= 5) strength += 1;
    
    // Contains uppercase letters
    if (password.match(/[A-Z]/)) strength += 1;
    
    // Contains lowercase letters
    if (password.match(/[a-z]/)) strength += 1;
    
    // Contains numbers
    if (password.match(/[0-9]/)) strength += 1;
    
    switch (strength) {
      case 0:
      case 1:
        setPasswordStrength('weak');
        break;
      case 2:
        setPasswordStrength('medium');
        break;
      case 3:
      case 4:
        setPasswordStrength('strong');
        break;
      default:
        setPasswordStrength('');
    }
  };
  
  // Get password strength color
  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak':
        return 'danger';
      case 'medium':
        return 'warning';
      case 'strong':
        return 'success';
      default:
        return 'secondary';
    }
  };
  
  const handleSubmit = async (e, addAnother = false) => { // Make async
    e.preventDefault();
    const form = e.currentTarget;
    
    // Reset errors
    setShowError(false);
    setErrorMessage('');
    
    // Extra validation for password match
    if (formData.password !== formData.confirmPassword) {
      setPasswordMatch(false);
      setValidated(true);
      return;
    }
    
    if (form.checkValidity() === false) {
      e.stopPropagation();
      setValidated(true);
      return;
    }
    
    setIsSubmitting(true); // Set submitting state

    // Construct payload for the API - ensure field names match backend DSOCreate schema
    const payload = {
      name: formData.name,
      email: formData.email,
      mobile: formData.phone, // Frontend uses 'phone', backend expects 'mobile'
      password: formData.password,
      state_id: formData.state || null, // Send ID directly
      city_id: formData.city || null,   // Send ID directly
      pincode_id: formData.pincode || null, // Send ID directly
      applicant_type: formData.applicantType || null,
      individual_type: formData.applicantType === 'Individual' ? formData.individualType || null : null,
      business_type: formData.applicantType === 'Business' ? formData.businessType || null : null,
      relationship_manager_id: formData.relationshipManager || null // Send ID directly
    };

    console.log('Submitting DSO data:', payload);

    try {
      const response = await dsoApi.createDso(payload);
      console.log('DSO creation successful:', response.data);
      setShowSuccess(true);
      
      if (addAnother) {
        // Reset form for adding another DSO
        setFormData({
          name: '',
          email: '',
          phone: '',
          state: '',
          city: '',
          pincode: '',
          applicantType: '',
          individualType: '',
          businessType: '',
          password: '',
          confirmPassword: '',
          relationshipManager: ''
        });
        setValidated(false);
        setPasswordStrength('');
        setPasswordMatch(true); // Reset password match state
        setCities([]); // Reset dependent dropdowns
        setPincodes([]); // Reset dependent dropdowns
        // Consider resetting Select components' visual state if needed
        
        // Clear success message after a short delay
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
      } else {
        // Redirect after success message delay
        setTimeout(() => {
          navigate('/admin/dso-list');
        }, 2000);
      }
    } catch (error) {
      console.error('Error creating DSO:', error);
      // Extract error message from backend response if available
      const apiErrorMessage = error.response?.data?.detail || error.message || 'Failed to add DSO. Please try again.';
      setErrorMessage(apiErrorMessage);
      setShowError(true);
    } finally {
       setIsSubmitting(false); // Reset submitting state
    }
  };
  
  return (
    <Container fluid className="p-3">
      {/* Header */}
      <Row className="mb-3">
        <Col>
          <h4 className="mb-1">
            <FaArrowLeft 
              className="me-2 cursor-pointer" 
              onClick={() => navigate('/admin/dso-list')}
              style={{ cursor: 'pointer' }}
            />
            Add New DSO
          </h4>
          <p className="text-muted small mb-0">Create a new DSO partner in the system</p>
        </Col>
      </Row>
      
      {/* Success and Error Alerts */}
      {showSuccess && (
        <Row className="mb-3">
          <Col>
            <Alert variant="success" onClose={() => setShowSuccess(false)} dismissible>
              DSO added successfully!
            </Alert>
          </Col>
        </Row>
      )}
      
      {showError && (
        <Row className="mb-3">
          <Col>
            <Alert variant="danger" onClose={() => setShowError(false)} dismissible>
              {errorMessage}
            </Alert>
          </Col>
        </Row>
      )}
      
      {/* Form */}
      <Card className="shadow-sm">
        <Card.Body>
          <Form noValidate validated={validated} onSubmit={(e) => handleSubmit(e)} className="compact-form">
            
            {/* Applicant Type Section */}
            <div className="applicant-section">
              <Row className="g-2">
                <Col md={4}>
                  <Form.Group>
                    <Select
                      name="applicantType"
                      value={formData.applicantType ? { value: formData.applicantType, label: formData.applicantType } : null}
                      onChange={(selectedOption) => {
                        handleApplicantTypeChange({ target: { value: selectedOption ? selectedOption.value : '' } });
                      }}
                      options={[
                        { value: 'Individual', label: 'Individual' },
                        { value: 'Business', label: 'Business' }
                      ]}
                      placeholder="Select Applicant Type"
                      className={validated && !formData.applicantType ? 'is-invalid' : ''}
                      classNamePrefix="react-select"
                      isClearable={false}
                    />
                    {validated && !formData.applicantType && (
                      <div className="invalid-feedback" style={{display: 'block'}}>
                        Please select applicant type.
                      </div>
                    )}
                  </Form.Group>
                </Col>

                {formData.applicantType === 'Individual' && (
                  <Col md={4}>
                    <Form.Group>
                      <Select
                        name="individualType"
                        value={formData.individualType ? { value: formData.individualType, label: formData.individualType } : null}
                        onChange={(selectedOption) => {
                          handleInputChange({ target: { name: 'individualType', value: selectedOption ? selectedOption.value : '' } });
                        }}
                        options={individualTypes.map(type => ({ value: type, label: type }))}
                        placeholder="Select Individual Type"
                        className={validated && !formData.individualType ? 'is-invalid' : ''}
                        classNamePrefix="react-select"
                        isClearable={false}
                      />
                      {validated && !formData.individualType && (
                        <div className="invalid-feedback" style={{display: 'block'}}>
                          Please select individual type.
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                )}

                {formData.applicantType === 'Business' && (
                  <Col md={4}>
                    <Form.Group>
                      <Select
                        name="businessType"
                        value={formData.businessType ? { value: formData.businessType, label: formData.businessType } : null}
                        onChange={(selectedOption) => {
                          handleInputChange({ target: { name: 'businessType', value: selectedOption ? selectedOption.value : '' } });
                        }}
                        options={businessTypes.map(type => ({ value: type, label: type }))}
                        placeholder="Select Business Type"
                        className={validated && !formData.businessType ? 'is-invalid' : ''}
                        classNamePrefix="react-select"
                        isClearable={false}
                      />
                      {validated && !formData.businessType && (
                        <div className="invalid-feedback" style={{display: 'block'}}>
                          Please select business type.
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                )}
              </Row>
            </div>

            {/* Relationship Manager Section */}
            <Row className="mb-3">
              <Col>
                <h6 className="section-header text-primary border-bottom">
                  <FaUserCheck className="me-2" /> Relationship Manager
                </h6>
                <Form.Group>
                  <Form.Label>Select Relationship Manager <span className="text-danger">*</span></Form.Label>
                  <Select
                    options={relationshipManagers}
                    isLoading={isLoadingRm}
                    isSearchable={true}
                    isClearable={true}
                    placeholder="Search and select a relationship manager..."
                    onChange={handleRMChange}
                    value={relationshipManagers.find(rm => rm.value === formData.relationshipManager)}
                    className={validated && !formData.relationshipManager ? 'is-invalid' : ''}
                    formatOptionLabel={option => (
                      <div className="d-flex flex-column">
                        <div>{option.label}</div>
                        <small className="text-muted">{option.email} • {option.location}</small>
                      </div>
                    )}
                    noOptionsMessage={() => "No relationship managers found"}
                    loadingMessage={() => "Loading relationship managers..."}
                  />
                  {validated && !formData.relationshipManager && (
                    <div className="invalid-feedback" style={{display: 'block'}}>
                      Please select a relationship manager.
                    </div>
                  )}
                </Form.Group>
              </Col>
            </Row>
            
            {/* Two Column Layout for the Rest of the Form */}
            <Row className="g-3">
              <Col md={6}>
                {/* Personal Information Section */}
                <h6 className="section-header text-primary border-bottom">
                  <FaUser className="me-2" /> Personal Information
                </h6>
                
                <Form.Group className="mb-3">
                  <InputGroup>
                    <InputGroup.Text><FaUser /></InputGroup.Text>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Full Name"
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a name.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <InputGroup>
                    <InputGroup.Text><FaEnvelope /></InputGroup.Text>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="Email Address"
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid email address.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <InputGroup>
                    <InputGroup.Text><FaPhone /></InputGroup.Text>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Phone Number (10 digits)"
                      pattern="[0-9]{10}"
                      required
                    />
                    <Form.Control.Feedback type="invalid">
                      Please provide a valid 10-digit phone number.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>

                {/* Location Information Section */}
                <h6 className="section-header text-primary border-bottom">
                  <FaMapMarkerAlt className="me-2" /> Location
                </h6>
                
                <Form.Group className="mb-2">
                  <Select
                    name="state"
                    value={states.find(option => option.value === formData.state)}
                    onChange={handleStateChange}
                    options={states}
                    isLoading={isLoadingStates}
                    placeholder="Select State"
                    className={validated && !formData.state ? 'is-invalid' : ''}
                    classNamePrefix="react-select"
                    isClearable
                  />
                  {validated && !formData.state && (
                    <div className="invalid-feedback" style={{display: 'block'}}>
                      Please select a state.
                    </div>
                  )}
                </Form.Group>
                
                <Row className="g-2">
                  <Col md={6}>
                    <Form.Group>
                      <Select
                        name="city"
                        value={cities.find(option => option.value === formData.city)}
                        onChange={handleCityChange}
                        options={cities}
                        placeholder="Select City"
                        isDisabled={!formData.state}
                        isLoading={isLoadingCities}
                        className={validated && !formData.city ? 'is-invalid' : ''}
                        classNamePrefix="react-select"
                        isClearable
                      />
                      {validated && !formData.city && (
                        <div className="invalid-feedback" style={{display: 'block'}}>
                          Please select a city.
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group>
                      <Select
                        name="pincode"
                        value={pincodes.find(option => option.value === formData.pincode)}
                        onChange={(selectedOption) => {
                          handleInputChange({ target: { name: 'pincode', value: selectedOption ? selectedOption.value : '' } });
                        }}
                        options={pincodes}
                        placeholder="Select Pincode"
                        isDisabled={!formData.city}
                        isLoading={isLoadingPincodes}
                        className={validated && !formData.pincode ? 'is-invalid' : ''}
                        classNamePrefix="react-select"
                        isClearable
                      />
                      {validated && !formData.pincode && (
                        <div className="invalid-feedback" style={{display: 'block'}}>
                          Please select a pincode.
                        </div>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
              
              <Col md={6}>
                {/* Account Security Section */}
                <h6 className="section-header text-primary border-bottom">
                  <FaLock className="me-2" /> Account Security
                </h6>
                
                <Form.Group className="mb-3">
                  <Form.Label>Create Password <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder="Create password"
                      pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&#]{5,}$"
                      required
                      aria-describedby="passwordHelpBlock"
                    />
                    <Button 
                      variant="outline-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      Password must be at least 5 characters with uppercase, lowercase, and numbers.
                    </Form.Control.Feedback>
                  </InputGroup>
                  <Form.Text id="passwordHelpBlock" muted>
                    Password strength: {passwordStrength && (
                      <span className={`text-${getPasswordStrengthColor()} fw-bold`}>
                        {passwordStrength.charAt(0).toUpperCase() + passwordStrength.slice(1)}
                      </span>
                    )}
                  </Form.Text>
                  <div className="password-checklist">
                    <div className={`d-flex align-items-center ${formData.password && formData.password.length >= 5 ? 'text-success' : 'text-muted'}`}>
                      <span className="me-1">✓</span> At least 5 characters
                    </div>
                    <div className={`d-flex align-items-center ${formData.password && formData.password.match(/[A-Z]/) ? 'text-success' : 'text-muted'}`}>
                      <span className="me-1">✓</span> At least one uppercase letter
                    </div>
                    <div className={`d-flex align-items-center ${formData.password && formData.password.match(/[a-z]/) ? 'text-success' : 'text-muted'}`}>
                      <span className="me-1">✓</span> At least one lowercase letter
                    </div>
                    <div className={`d-flex align-items-center ${formData.password && formData.password.match(/[0-9]/) ? 'text-success' : 'text-muted'}`}>
                      <span className="me-1">✓</span> At least one number
                    </div>
                  </div>
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Confirm Password <span className="text-danger">*</span></Form.Label>
                  <InputGroup>
                    <Form.Control
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      placeholder="Confirm password"
                      required
                      isInvalid={validated && !passwordMatch}
                    />
                    <Button 
                      variant="outline-secondary"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      Passwords do not match.
                    </Form.Control.Feedback>
                  </InputGroup>
                </Form.Group>
              </Col>
            </Row>
            
            <hr className="my-3" />
            
            <Row>
              <Col className="d-flex gap-2 action-buttons">
                <Button
                  variant="primary"
                  type="submit"
                  size="sm"
                  disabled={isSubmitting} // Disable button while submitting
                >
                  {isSubmitting ? 'Saving...' : <><FaSave className="me-1" /> Save DSO</>}
                </Button>
                
                <Button
                  variant="outline-primary"
                  onClick={(e) => handleSubmit(e, true)}
                  size="sm"
                  disabled={isSubmitting} // Disable button while submitting
                >
                  {isSubmitting ? 'Saving...' : <><FaPlus className="me-1" /> Save & Add Another</>}
                </Button>
                  
                <Button
                  variant="outline-secondary" 
                  onClick={() => navigate('/admin/dso-list')}
                  size="sm"
                >
                  Cancel
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default AddDSO;
